import {Box, Hide, HStack} from 'platform/foundation';
import {getDateTimeFormatPlaceholder, useDateTimeFormatter, useLocale} from 'platform/locale';
import {cva} from 'platform/shared';
import {match, Pattern} from 'ts-pattern';

import {ClipboardEvent, FocusEvent, KeyboardEvent, useRef, useState} from 'react';

import {always, isEmpty, isNil, isNotNil} from 'ramda';
import {isNotNilOrEmpty, isTrue, noop} from 'ramda-adjunct';

import {getSafely, suffixTestId, TestIdProps, useBoolean, useOnMount} from 'shared/web';

import {FormControlProps} from '../../types/FormControlProps';
import {TimeType} from '../../types/TimeType';
import {getTimeFromDate} from '../../utils/getTimeFromDate';
import {setTimeOnDate} from '../../utils/setTimeOnDate';
import {useAnimatedPopper} from '../AnimatedPopper/hooks/useAnimatedPopper';
import {DatePickerInput} from '../DatePicker/components/DatePickerInput';
import {Separator} from '../Separator/Separator';
import {useTranslationContext} from '../TranslationProvider/TranslationContext';
import {TimePickerRelativeTimes} from './components/TimePickerRelativeTimes';
import {useTimePickerFocusManagement} from './hooks/useTimePickerFocusManagement';
import {RelativeTimeOption, ResolvedRelativeTime} from './types/RelativeTime';
import {filterTimeOptions} from './utils/filterTimeOptions';
import {getNextRoundedTime} from './utils/getNextRoundedTime';
import {getTimeFromStringInput} from './utils/getTimeFromStringInput';
import {getTimeOptions} from './utils/getTimeOptions';
import {isTimeDisabled} from './utils/isTimeDisabled';
import {resolveRelativeTimes} from './utils/resolveRelativeTimes';
import {scrollOptionIntoList} from './utils/scrollOptionIntoList';

/** Fixed reference date that is never affected by DST transitions. */
const REFERENCE_DATE = new Date(2000, 0, 1); // eslint-disable-line eag/no-native-date-math -- Pre-existing native Date math, migrate to date-fns when possible
const POPPER_VIEWPORT_PADDING = 8;

const selectionVariants = cva('w-full whitespace-nowrap rounded-[var(--radius-small)] p-2', {
  variants: {
    state: {
      default:
        'cursor-pointer bg-[initial] text-[var(--color-primary)] hover:bg-[var(--color-palettes-blue-10-100)] hover:text-[var(--color-palettes-blue-80-100)]',
      selected:
        'cursor-pointer bg-[var(--color-palettes-blue-10-100)] text-[var(--color-palettes-blue-80-100)] hover:bg-[var(--color-palettes-blue-10-100)] hover:text-[var(--color-palettes-blue-80-100)]',
      disabled: 'cursor-not-allowed bg-[initial] text-[var(--color-palettes-neutral-900-40)]',
      disabledSelected:
        'cursor-not-allowed bg-[var(--color-palettes-blue-10-100)] text-[var(--color-palettes-neutral-900-40)]',
    },
    isFocused: {
      true: 'bg-[var(--color-palettes-blue-10-100)] [outline:2px_solid_var(--color-palettes-blue-60-100)] [outline-offset:1px]',
      false: '',
    },
  },
  defaultVariants: {
    state: 'default',
    isFocused: false,
  },
});

const listClassName =
  'flex h-60 w-35 flex-col gap-px overflow-y-auto p-2 [scrollbar-gutter:stable]';

function getSelectionState(isSelected: boolean, isDisabled: boolean) {
  return match({isSelected, isDisabled})
    .with({isDisabled: true, isSelected: true}, () => 'disabledSelected' as const)
    .with({isDisabled: true}, () => 'disabled' as const)
    .with({isSelected: true}, () => 'selected' as const)
    .otherwise(() => 'default' as const);
}

export interface TimePickerProps extends FormControlProps<TimeType | null>, TestIdProps {
  /**
   * @about Minimum time that can be selected. Defaults to 00:00.
   */
  minTime?: TimeType;
  /**
   * @about Maximum time that can be selected. Defaults to 23:59.
   */
  maxTime?: TimeType;
  /**
   * @about Quick-select chips rendered beside the list. 'currentTime' renders a "Now"
   * chip committing the exact current time. No chips are rendered when omitted.
   */
  relativeTimes?: RelativeTimeOption[];
  placeholder?: string;
  isRecommended?: boolean;
  /**
   * @about Enable keyboard navigation for the TimePicker. Defaults to true.
   */
  keyboardNavigationEnabled?: boolean;
  /**
   * @about Enable focus trap within the dropdown. Defaults to true.
   */
  focusTrapEnabled?: boolean;
  /**
   * @about Enable non-clearable mode. Defaults to false.
   */
  isNonClearable?: boolean;
  /**
   * @about Accessible name for the input when no visible `label` is rendered. Ignored
   * whenever `label` is set, so the visible label always wins.
   */
  'aria-label'?: string;
}

// eslint-disable-next-line max-lines-per-function, max-statements -- Pre-existing violation
export function TimePicker(props: TimePickerProps) {
  const {localeConfig, language} = useLocale();
  const formatDateTime = useDateTimeFormatter();
  const t = useTranslationContext();

  const [inputValue, setInputValue] = useState<string | null>(null);
  const [isFocused, focusInput, blurInput] = useBoolean();
  const inputRef = useRef<HTMLInputElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);
  // Ref-callback scroll must fire once per dropdown open, not on every re-render while open.
  const hasScrolledSinceOpenRef = useRef(false);
  // Keeps keyboard-focus scrolling to one call per highlight change.
  const lastFocusScrollIndexRef = useRef<number | null>(null);
  const blurDeferTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    popperProps,
    Popper,
    openPopper,
    closePopper: originalClosePopper,
    referenceRef,
    isOpen,
  } = useAnimatedPopper({
    // Matches DatePicker so a date + time pair opens in the same direction.
    placement: 'bottom-start',
    gutter: 4,
    // Re-anchor to the input on scroll/resize; without it the popper is positioned once and
    // stays stranded at its initial screen coordinates while the page scrolls.
    autoUpdateOptions: 'default',
    // Flip above the input before the cross-axis shift can slide the list over it (T20-140516).
    isFlipPrioritized: true,
    flipOptions: {crossAxis: false},
    shiftOptions: {
      padding: POPPER_VIEWPORT_PADDING,
      crossAxis: true,
    },
  });

  useOnMount(function registerBlurDeferTimeoutCleanup() {
    return function clearBlurDeferTimeoutOnUnmount() {
      if (blurDeferTimeoutRef.current !== null) {
        clearTimeout(blurDeferTimeoutRef.current);
      }
    };
  });

  const isInvalid = props.isInvalid || !!props.errorMessage;

  const formatTimeLabel = (time: TimeType) =>
    formatDateTime('timeShort', setTimeOnDate(REFERENCE_DATE, time));

  const formattedValue = isNotNil(props.value) ? formatTimeLabel(props.value) : '';

  // The interval is fixed at 15 minutes by design — any other minute stays
  // reachable by typing.
  const options = getTimeOptions();

  const isFiltering = isFocused && isNotNil(inputValue) && inputValue !== formattedValue;
  const filteredOptions = isFiltering
    ? filterTimeOptions(options, inputValue, formatTimeLabel)
    : options;
  // A typed exact time (e.g. 15:07) matches no rounded option — keep the full list visible.
  const visibleOptions = isEmpty(filteredOptions) ? options : filteredOptions;

  const relativeTimes = isOpen
    ? resolveRelativeTimes({
        relativeTimes: props.relativeTimes ?? [],
        now: new Date(),
        minTime: props.minTime,
        maxTime: props.maxTime,
        formatTimeLabel,
        t,
      })
    : [];

  const scrollTargetTime = match(props.value)
    .with(Pattern.nonNullable, (value) =>
      options.includes(value) ? value : getNextRoundedTime(setTimeOnDate(REFERENCE_DATE, value))
    )
    .otherwise(() => getNextRoundedTime(new Date()));

  const isKeyboardNavigationEnabled = props.keyboardNavigationEnabled !== false;
  const isFocusTrapEnabled = props.focusTrapEnabled !== false;

  const {
    focusedOptionIndex,
    areChipsFocused,
    chipFocusProps,
    moveOptionFocus,
    focusChips,
    focusList,
    resetFocus,
  } = useTimePickerFocusManagement({
    visibleOptions,
    value: props.value,
    scrollTargetTime,
    isOptionDisabled: (option) => isTimeDisabled(option, props.minTime, props.maxTime),
    inputRef,
  });

  // No isOpen guard: the click that follows an outside-click close reads a stale
  // isOpen from its render closure and would refuse to reopen the dropdown.
  const openDropdown = () => {
    hasScrolledSinceOpenRef.current = false;
    openPopper();
  };

  const closeDropdown = () => {
    // A pending deferred-blur commit belongs to the interaction that just ended —
    // letting it fire after an explicit commit/clear would emit onChange twice.
    if (isNotNil(blurDeferTimeoutRef.current)) {
      clearTimeout(blurDeferTimeoutRef.current);
      blurDeferTimeoutRef.current = null;
    }
    resetFocus();
    lastFocusScrollIndexRef.current = null;
    hasScrolledSinceOpenRef.current = false;
    originalClosePopper();
  };

  const commitInputValue = (rawValue: string) => {
    const parsedTime = getTimeFromStringInput(rawValue);

    match({isEmptyInput: isEmpty(rawValue.trim()), parsedTime})
      .with({isEmptyInput: true}, () => {
        if (!props.isNonClearable) {
          props.onChange?.(null);
        }
      })
      .with({parsedTime: Pattern.nonNullable}, ({parsedTime: time}) => {
        if (!isTimeDisabled(time, props.minTime, props.maxTime)) {
          props.onChange?.(time);
        }
      })
      // Unparseable input reverts to the previous value silently.
      .otherwise(noop);
  };

  const commitTime = (time: TimeType) => {
    blurInput();
    setInputValue(null);
    props.onChange?.(time);
    closeDropdown();
  };

  const handleOptionClick = (option: TimeType, isOptionDisabled: boolean) => {
    if (isOptionDisabled) {
      return;
    }
    commitTime(option);
  };

  const handleClearInput = () => {
    if (props.isNonClearable) {
      return;
    }
    blurInput();
    setInputValue(null);
    props.onChange?.(null);
    closeDropdown();
  };

  const handleInputChange = (value: string | null) => {
    // A change can arrive while isFocused is stale (e.g. typing right after Enter
    // committed) — mark the input focused so the edit buffer drives the display.
    focusInput();
    setInputValue(value);
    // Typing re-filters the list, so any previous highlight points at a stale index.
    resetFocus();
    lastFocusScrollIndexRef.current = null;
    // Plain openPopper: typing must not reset the scroll flag on every keystroke.
    openPopper();
  };

  const commitInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    blurInput();
    // Read the live DOM value: after an option click already committed, the input has
    // re-rendered to the fresh formatted value and the commit below becomes a no-op.
    const rawValue = event.target.value;

    if (rawValue !== formattedValue) {
      commitInputValue(rawValue);
    }

    setInputValue(null);
    // Always close: leaving the field must never strand an open dropdown, even when
    // the text is unchanged (e.g. Shift+Tab straight after opening).
    closeDropdown();
    props.onBlur?.(event);
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
    const relatedTarget = event.relatedTarget instanceof Node ? event.relatedTarget : null;
    const isFocusInsidePopper =
      isNotNil(relatedTarget) && (popperRef.current?.contains(relatedTarget) ?? false);

    match({
      keepsFocusInsidePopper: isOpen && isFocusInsidePopper,
      shouldDeferCommit: isOpen && isNil(relatedTarget),
    })
      .with({keepsFocusInsidePopper: true}, noop)
      // Clicking a non-focusable target blurs the input with a null relatedTarget —
      // defer the commit so an option/chip/clear click handler wins first.
      .with({shouldDeferCommit: true}, () => {
        if (isNotNil(blurDeferTimeoutRef.current)) {
          clearTimeout(blurDeferTimeoutRef.current);
        }

        blurDeferTimeoutRef.current = setTimeout(() => {
          blurDeferTimeoutRef.current = null;

          // The picker may have unmounted while the timeout was pending.
          if (isNil(inputRef.current)) {
            return;
          }

          if (popperRef.current?.contains(document.activeElement)) {
            return;
          }

          commitInputBlur(event);
        }, 0);
      })
      .otherwise(() => commitInputBlur(event));
  };

  const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
    // Re-seed only on a genuinely fresh focus, not on mid-edit focus round-trips.
    const wasFocused = isFocused;
    focusInput();
    if (!wasFocused) {
      setInputValue(formattedValue);
    }
    props.onFocus?.(event);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    match(event.key)
      .with('Enter', () => {
        event.preventDefault();
        // Commit directly — programmatic blur() is a no-op when the element is not
        // truly focused, so the blur path cannot be relied on here.
        const rawValue = inputValue ?? formattedValue;
        const highlightedOption = isNotNil(focusedOptionIndex)
          ? getSafely(visibleOptions, focusedOptionIndex)
          : null;

        match({hasTypedText: rawValue !== formattedValue, highlightedOption})
          // Typing wins over the list highlight (ticket: "field holds 15:30").
          .with({hasTypedText: true}, () => {
            commitInputValue(rawValue);
            blurInput();
            setInputValue(null);
            closeDropdown();
          })
          .with({highlightedOption: Pattern.nonNullable}, ({highlightedOption: option}) => {
            if (!isTimeDisabled(option, props.minTime, props.maxTime)) {
              commitTime(option);
            }
          })
          .otherwise(() => {
            blurInput();
            setInputValue(null);
            closeDropdown();
          });
      })
      .with('Escape', () => {
        event.preventDefault();
        event.stopPropagation();
        // Discard any typed text so a later blur cannot commit it.
        setInputValue(formattedValue);
        closeDropdown();
      })
      .with('ArrowDown', 'ArrowUp', () => {
        if (!isKeyboardNavigationEnabled) {
          return;
        }
        event.preventDefault();
        // Plain openPopper: arrow navigation positions via the focus highlight.
        openPopper();
        match(event.key)
          .with('ArrowDown', () => moveOptionFocus(1))
          .otherwise(() => moveOptionFocus(-1));
      })
      .with('Tab', () => {
        if (
          !isOpen ||
          event.shiftKey ||
          !isKeyboardNavigationEnabled ||
          !isFocusTrapEnabled ||
          // Tab from the list is the exit — let focus leave; blur closes the dropdown.
          isNotNil(focusedOptionIndex)
        ) {
          return;
        }
        event.preventDefault();
        match(isEmpty(relativeTimes))
          .with(true, () => focusList())
          .with(false, () => focusChips())
          .exhaustive();
      })
      .otherwise(noop);
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const pastedText = event.clipboardData?.getData('text/plain') ?? '';
    const parsedTime = getTimeFromStringInput(pastedText);

    if (isNotNil(parsedTime) && !isTimeDisabled(parsedTime, props.minTime, props.maxTime)) {
      event.preventDefault();
      props.onChange?.(parsedTime);
      setInputValue(null);
      blurInput();
      closeDropdown();
    }
  };

  const handlePopperKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    match(event.key)
      .with('Tab', () => {
        // Tab from the chips panel moves on to the list (Shift+Tab is handled
        // by the chips component itself and returns to the input).
        if (areChipsFocused && !event.shiftKey && isFocusTrapEnabled) {
          event.preventDefault();
          focusList();
        }
      })
      .otherwise(noop);
  };

  const handleChipsEscape = () => {
    closeDropdown();
    inputRef.current?.focus();
  };

  const handleRelativeTimeSelect = (relativeTime: ResolvedRelativeTime) => {
    // The Now chip commits the moment of the click, not the render-time snapshot;
    // if the fresh time drifted past a min/max bound, the (gated) snapshot wins.
    const currentTime = getTimeFromDate(new Date());
    const time = match({
      isNow: relativeTime.isNow,
      isCurrentTimeDisabled: isTimeDisabled(currentTime, props.minTime, props.maxTime),
    })
      .with({isNow: true, isCurrentTimeDisabled: false}, () => currentTime)
      .otherwise(() => relativeTime.time);
    commitTime(time);
  };

  const placeholder = match([isNotNilOrEmpty(props.placeholder), isTrue(props.isDisabled)])
    .with([true, Pattern.boolean], always(props.placeholder))
    .with([false, true], always(null))
    .otherwise(
      always(getDateTimeFormatPlaceholder(localeConfig.dateTimeFormat.timeShort, language))
    );

  return (
    <div>
      <div ref={referenceRef}>
        <DatePickerInput
          ref={inputRef}
          id={props.id}
          leftIcon="devices/access_time"
          onClick={props.isDisabled ? undefined : openDropdown}
          onBlur={handleBlur}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          value={isFocused ? inputValue : formattedValue}
          onChange={handleInputChange}
          label={props.label}
          aria-label={props['aria-label']}
          tooltip={props.tooltip}
          isInvalid={isInvalid}
          isRequired={props.isRequired}
          isDisabled={props.isDisabled}
          errorMessage={props.errorMessage}
          helperText={props.helperText}
          placeholder={placeholder}
          autoComplete="off"
          isRecommended={props.isRecommended}
          data-testid={suffixTestId('input', props)}
          onClearInput={props.isNonClearable ? undefined : handleClearInput}
        />
      </div>
      <Popper {...popperProps}>
        <div
          ref={popperRef}
          tabIndex={-1}
          onKeyDown={handlePopperKeyDown}
          data-testid={suffixTestId('dropdown', props)}
          className="[outline:none]"
        >
          <Box
            borderRadius="medium"
            boxShadow="elevation_2"
            border="1px solid"
            borderColor="palettes.neutral.40.100"
            backgroundColor="general.white"
          >
            <HStack>
              <Hide when={isEmpty(relativeTimes)}>
                <Box padding={2}>
                  <TimePickerRelativeTimes
                    relativeTimes={relativeTimes}
                    value={props.value}
                    onSelect={handleRelativeTimeSelect}
                    onEscapeKey={handleChipsEscape}
                    isFocused={chipFocusProps.isFocused}
                    focusedIndex={chipFocusProps.focusedIndex}
                    onFocusChange={chipFocusProps.onFocusChange}
                    onBlur={chipFocusProps.onBlur}
                    data-testid={props['data-testid']}
                  />
                </Box>
                <Separator orientation="vertical" spacing={0} />
              </Hide>
              <div
                role="listbox"
                aria-label="Time options"
                data-testid={suffixTestId('timeList', props)}
                className={listClassName}
              >
                {visibleOptions.map((option, index) => {
                  const isOptionDisabled = isTimeDisabled(option, props.minTime, props.maxTime);
                  const isSelected = option === props.value;
                  const isOptionFocused = index === focusedOptionIndex;
                  return (
                    <div
                      key={option}
                      role="option"
                      aria-selected={isSelected}
                      aria-disabled={isOptionDisabled}
                      className={selectionVariants({
                        state: getSelectionState(isSelected, isOptionDisabled),
                        isFocused: isOptionFocused,
                      })}
                      ref={(element) => {
                        if (
                          option === scrollTargetTime &&
                          isNotNil(element) &&
                          !hasScrolledSinceOpenRef.current
                        ) {
                          hasScrolledSinceOpenRef.current = true;
                          scrollOptionIntoList(element);
                        }
                        if (
                          isOptionFocused &&
                          isNotNil(element) &&
                          lastFocusScrollIndexRef.current !== index
                        ) {
                          lastFocusScrollIndexRef.current = index;
                          scrollOptionIntoList(element);
                        }
                      }}
                      onClick={() => handleOptionClick(option, isOptionDisabled)}
                      data-testid={suffixTestId(`timeOption-${option}`, props)}
                    >
                      {formatTimeLabel(option)}
                    </div>
                  );
                })}
              </div>
            </HStack>
          </Box>
        </div>
      </Popper>
    </div>
  );
}
